import {
  DynamicForm,
  type DynamicFormFieldType,
  type DynamicFormRef,
  type OdinKey,
  type RadioGroupResult,
} from '@myosh/odin-components';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useGetLocalesQuery } from '../../../redux/services/api';
import {
  type FormBuilderFormGroup,
  selectSelectedGroup,
  setSelectedGroupId,
  updateFormGroup,
} from '../../../redux/slices/form-builder-slice';
import CloseActiveItemButton from '../../common/close-active-item-button';
import { createTranslations } from '../form-builder-utils';
import { GroupDetailsFormSettings, groupDisplayTypes } from '../util/group-details.util';

function FormBuilderGroupDetails() {
  const selectedGroup = useAppSelector(selectSelectedGroup) as FormBuilderFormGroup;
  const dispatch = useAppDispatch();

  const formIdRef = useRef(crypto.randomUUID());
  const dynamicFormRef = useRef<DynamicFormRef>(null);

  const selectedGroupRef = useRef(selectedGroup);
  selectedGroupRef.current = selectedGroup;

  const { data: locales } = useGetLocalesQuery();
  const localeIdsRef = useRef(locales);
  localeIdsRef.current = locales;

  const loadGroupData = useCallback((group: FormBuilderFormGroup) => {
    dynamicFormRef.current?.loadData({
      caption: group.caption?.translations[0]?.value ?? null,
      displayType: groupDisplayTypes.find((type) => type.value === group.displayType) ?? groupDisplayTypes[0],
    });
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      dynamicFormRef.current?.resetFormState({});
      loadGroupData(selectedGroup);
    }
  }, [selectedGroup, loadGroupData]);

  const onCloseGroupDetailsClick = useCallback(() => {
    dispatch(setSelectedGroupId(undefined));
  }, [dispatch]);

  const handleChange = useMemo(
    () =>
      debounce(async (_fieldId: OdinKey, fieldName: string, _fieldType: DynamicFormFieldType, fieldValue: unknown) => {
        const errors = dynamicFormRef.current?.getFieldErrors() ?? {};
        if (errors[fieldName] || !selectedGroupRef.current || !localeIdsRef.current) {
          return;
        }

        let value = fieldValue;
        if ('caption' === fieldName) {
          value = { translations: createTranslations(String(fieldValue ?? ''), localeIdsRef.current) };
        } else if ('displayType' === fieldName) {
          value = (fieldValue as RadioGroupResult)?.value ?? '';
        }

        dispatch(
          updateFormGroup({
            id: selectedGroupRef.current.id,
            uuid: selectedGroupRef.current.uuid,
            [fieldName]: value,
          })
        );
      }, 400),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      handleChange.cancel();
    };
  }, [handleChange]);

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between py-2">
        <h2 className="text-lg font-semibold">Section Details</h2>
        <CloseActiveItemButton onClick={onCloseGroupDetailsClick} />
      </div>
      <DynamicForm
        ref={dynamicFormRef}
        dynamicFormId={formIdRef.current}
        settings={GroupDetailsFormSettings}
        onSubmit={() => {}}
        data={{}}
        formMode="all"
        onFieldChanged={handleChange}
      />
    </div>
  );
}

export default React.memo(FormBuilderGroupDetails);
